/**
 * Request Page - Payment Request Management
 * Refactored into modular components for maintainability
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AnimatePresence, motion } from 'framer-motion';
import { 
  createPaymentRequest, listPaymentRequests, cancelPaymentRequest,
  getCurrencies
} from '../lib/api';
import { toast } from 'sonner';
import { QrCode, Plus, Loader2, Clock, CheckCircle2 } from 'lucide-react';

// Refactored request components
import {
  RequestStats,
  RequestItem,
  CreateRequestDialog,
  QRCodeDialog
} from '../components/request';

const Request = () => {
  const [requests, setRequests] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [fixedAmount, setFixedAmount] = useState(true);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [note, setNote] = useState('');
  const [expiresIn, setExpiresIn] = useState('24');
  
  // QR Dialog state
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Memoized computed values
  const totalReceived = useMemo(() => 
    requests.reduce((sum, r) => sum + (r.total_received || 0), 0),
    [requests]
  );
  
  const completedCount = useMemo(() => 
    requests.filter(r => r.status === 'completed').length,
    [requests]
  );
  
  const pendingRequests = useMemo(() => 
    requests.filter(r => r.status === 'pending'),
    [requests]
  );
  
  const completedRequests = useMemo(() => 
    requests.filter(r => r.status !== 'pending'),
    [requests]
  );

  // Data fetching
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [requestsRes, currenciesRes] = await Promise.all([
        listPaymentRequests(),
        getCurrencies()
      ]);
      setRequests(requestsRes.data.requests || []);
      setCurrencies(currenciesRes.data || []);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Event handlers
  const handleCreate = async () => {
    if (fixedAmount && (!amount || parseFloat(amount) <= 0)) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setCreating(true);
    try {
      const { data } = await createPaymentRequest({
        amount: fixedAmount ? parseFloat(amount) : null,
        currency,
        note: note || undefined,
        expires_in_hours: parseInt(expiresIn)
      });
      
      toast.success('Payment request created');
      setShowCreateDialog(false);
      setAmount('');
      setNote('');
      
      // Show the QR code immediately
      setSelectedRequest({
        id: data.id,
        payment_link: data.payment_link,
        qr_data: data.qr_data,
        amount: data.amount,
        currency: data.currency,
        note: data.note,
        expires_at: data.expires_at,
        status: 'pending'
      });
      setShowQRDialog(true);
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (requestId) => {
    try {
      await cancelPaymentRequest(requestId);
      toast.success('Request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleShare = async (request) => {
    const shareData = {
      title: 'Payment Request',
      text: request.note || `Pay me on ParachPay`,
      url: request.payment_link || `${window.location.origin}/pay/${request.id}`
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        navigator.clipboard.writeText(shareData.url);
        toast.success('Link copied to clipboard');
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      toast.success('Link copied to clipboard');
    }
  };

  const showQR = (request) => {
    const paymentLink = `${window.location.origin}/pay/${request.id}`;
    setSelectedRequest({
      ...request,
      payment_link: paymentLink,
      qr_data: paymentLink
    });
    setShowQRDialog(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="space-y-6 max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="request-container"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Request Money</h1>
            <p className="text-muted-foreground">Create QR codes and payment links</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="rounded-xl" data-testid="create-request-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        </div>

        {/* Quick Stats */}
        <RequestStats
          pendingCount={pendingRequests.length}
          completedCount={completedCount}
          totalReceived={totalReceived}
          totalRequests={requests.length}
        />

        {/* Tabs */}
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-12 rounded-xl">
            <TabsTrigger value="active" className="rounded-lg" data-testid="active-tab">
              <Clock className="w-4 h-4 mr-2" />
              Active ({pendingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg" data-testid="history-tab">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              History ({completedRequests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-6">
            {pendingRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <QrCode className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-heading font-semibold mb-2">No Active Requests</h3>
                  <p className="text-muted-foreground mb-4">Create a payment request to get started</p>
                  <Button onClick={() => setShowCreateDialog(true)} className="rounded-xl">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Request
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <AnimatePresence>
                  {pendingRequests.map((request) => (
                    <RequestItem
                      key={request.id}
                      request={request}
                      onShowQR={showQR}
                      onShare={handleShare}
                      onCancel={handleCancel}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            {completedRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No completed requests yet
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedRequests.map((request) => (
                  <RequestItem
                    key={request.id}
                    request={request}
                    isHistory
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Create Request Dialog */}
      <CreateRequestDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        fixedAmount={fixedAmount}
        setFixedAmount={setFixedAmount}
        amount={amount}
        setAmount={setAmount}
        currency={currency}
        setCurrency={setCurrency}
        note={note}
        setNote={setNote}
        expiresIn={expiresIn}
        setExpiresIn={setExpiresIn}
        currencies={currencies}
        onCreate={handleCreate}
        creating={creating}
      />

      {/* QR Code Dialog */}
      <QRCodeDialog
        open={showQRDialog}
        onOpenChange={setShowQRDialog}
        selectedRequest={selectedRequest}
        onShare={handleShare}
      />
    </Layout>
  );
};

export default Request;
